// @flow

import { normalize, denormalize, schema } from 'normalizr';
import {
  camelizeKeys,
  decamelizeKeys,
  excludedAttributes,
  excludedKeys,
} from '@flow/libs/utils/camelizer';

import {
  extractResponseBodyAsJSON,
  get,
  getMultiple,
  post,
  put,
  remove,
} from '@flow/libs/oneflow-client/core';
import type { SortParams } from '@flow/libs/oneflow-client/core';

export const SHARED_WITH_ME_ID = 0;

export const camelizeAutomationRule = (obj: any) => camelizeKeys(obj, {
  process: (key, convert) => {
    if (/.+:.+/.test(key) || excludedAttributes.includes(key)) {
      return key;
    }
    return convert(key);
  },
  excludedKeys,
});

export const decamelizeAutomationRule = (
  automationRule: AutomationRule,
) => decamelizeKeys(automationRule, {
  process: (key, convert) => {
    if (excludedAttributes.includes(key)) {
      return key;
    }
    return convert(key);
  },
  excludedKeys,
  separator: '_',
});

export const automationRuleSchema = new schema.Entity('automationRules');

export const automationRuleDenormalizeSchema = new schema.Entity('automationRules');

export type NormalizedAutomationRules = {
  entities: {
    automationRules: {
      [number]: AutomationRule,
    },
  },
  result: number,
  count: number,
}

const normalizeAutomationRules = (automationRules) => ({
  ...normalize(automationRules.collection, [automationRuleSchema]),
  count: automationRules.count,
});

export const normalizeAutomationRule = (automationRule) => ({
  ...normalize(automationRule, automationRuleSchema),
  count: 1,
});

export const denormalizeAutomationRule = (automationRule) => (
  decamelizeKeys(denormalize(automationRule, automationRuleDenormalizeSchema), {
    separator: '_',
  })
);

type CreateAutomationRule = ({
  workspaceId: number,
  key: string,
  days: number,
  active: number,
}) => Promise<NormalizedAutomationRules>;

export const createAutomationRule: CreateAutomationRule = ({
  workspaceId,
  key,
  active,
  days,
  // eslint-disable-next-line no-shadow
}) => (
  post({
    url: '/automations/',
    body: {
      workspace_id: workspaceId,
      key,
      active,
      days,
    },
  })
    .then((response) => response.json())
    .then(camelizeAutomationRule)
    .then(normalizeAutomationRule)
);

export type AutomationRuleRequestParams = {
  url: string,
  params?: {},
  pagination?: {},
  sorting?: SortParams,
  sort?: Array<string>,
};

export const getAutomationRules = ({
  params: {
    workspaceId,
  },
  pagination,
  sort,
}) => (
  getMultiple({
    url: '/automations/',
    pagination,
    params: {
      'workspace_ids[]': workspaceId,
    },
    sort,
  })
    .then((response) => response.json())
    .then(camelizeAutomationRule)
    .then(normalizeAutomationRules)
);

export const getAutomationRule = ({ id }) => (
  get({
    url: `/automations/${id}`,
  })
    .then((response) => response.json())
    .then(camelizeAutomationRule)
    .then(normalizeAutomationRule)
);

type UpdateAutomationRule = ({
  id: number,
  days?: number,
  trigger?: Object,
  action?: Object,
  active?: boolean,
  workspaceId?: number,
}) => Promise<NormalizedAutomationRules>;

export const updateAutomationRule: UpdateAutomationRule = (props) => {
  const {
    id, active, days,
  } = props;
  return (
    put({
      url: `/automations/${id}`,
      body: decamelizeKeys({
        active,
        days,
      }, {
        separator: '_',
      }),
    })
      .then((response) => response.json())
      .then(camelizeAutomationRule)
      .then(normalizeAutomationRule)
  );
};

export const removeAutomationRule = ({ id }) => (
  remove({
    url: `/automations/${id}`,
  })
    .then(extractResponseBodyAsJSON)
);
